import { IStudentSessionReportViewModel, StudentSessionReportManagersConfig } from "../Interfaces/IStudentSessionReportViewModel";
import { StudentSessionContext } from "Shared/Models/StudentSessionReport";
import { container } from "tsyringe";
import { LookupStore } from "Stores/Domain/LookupStore";
import { KeyValuePair, isEmptyOrWhitespace } from "@shoothill/core";
import { UpsertStudentSessionRequest } from "../../Endpoints/Commands/UpsertStudentSession/Request";
import { UpsertStudentSessionRequestBuilder } from "../../Endpoints/Commands/UpsertStudentSession/RequestBuilder";
import { GetNewStudentSessionReportContextResponse, StudentBehaviourResponse } from "Views/Mentors/Mentor/WeeklyRota/Endpoints/GetNewStudentSessionReportContext/Response";
import { StudentSessionContextByIdResponse, StudentBehaviourWithSessionResponse } from "../../Endpoints/Querries/GetStudentSessionContextByIdEndpoint";
import { StudentSessionReportResponse } from "../../../../Student/StudentReports/Endpoints/Shared/StudentSessionContextResponse";
import { SaveActivityOptionsResponse } from "../../Endpoints/Commands/SaveActivityOptionsEndpoint";
import { SessionDocument, StudentEngagementModel, Mentor } from "Shared/Models/StudentSessionReport";
import { StudentTargetModel } from "Views/Shared/Views/StudentTarget/StudentTargetModel";
import { StudentSubTargetModel } from "Views/Shared/Views/StudentSubTarget/StudentSubTargetModel";
import { StudentSessionTargetModel } from "Views/Shared/Views/StudentSessionTarget/StudentSessionTargetModel";
import { StudentSessionSubTargetModel } from "Views/Shared/Views/StudentSessionSubTarget/StudentSessionSubTargetModel";
import { StudentSessionAQACertificateModel } from "Views/Shared/Views/StudentSessionAQACertificate/Shared/StudentSessionAQACertificateModel";
import { ActivityType } from "Application/Models/Domain";
import { convertTimeToDate } from "Application/Helpers/ConvertTimeToDate";
import { StudentSessionLegacyFormModel } from "Views/Shared/Views/StudentSessionLegacyForm/StudentSessionLegacyFormModel";
import { StudentSessionLegacyFormViewModel } from "Views/Shared/Views/StudentSessionLegacyForm/StudentSessionLegacyFormViewModel";
import { StudentTargetViewModel } from "Views/Shared/Views/StudentTarget/StudentTargetViewModel";
import { StudentSubTargetViewModel } from "Views/Shared/Views/StudentSubTarget/StudentSubTargetViewModel";
import { StudentSessionSubTargetViewModel } from "Views/Shared/Views/StudentSessionSubTarget/StudentSessionSubTargetViewModel";
import { StudentSessionAQACertificatesViewModel } from "Views/Shared/Views/StudentSessionAQACertificate/StudentSessionAQACertificatesViewModel";
import { StudentTargetResponse } from "Views/Shared/Endpoints/Response/Student";
import { StudentBehaviourModel } from "Views/Shared/Views/StudentBehaviour/StudentBehaviourModel";
import { StudentBehaviourViewModel } from "Views/Shared/Views/StudentBehaviour/StudentBehaviourViewModel";
import { StudentSessionBehaviourModel } from "Views/Shared/Views/StudentSessionBehaviour/StudentSessionBehaviourModel";
import { MentorModel, StudentModel } from "Shared/Models/StudentSessionReport/SupportingTypes";
import { StudentAbsenceModel } from "../../../../Student/StudentReports/StudentAbsence/StudentAbsenceModel";

/**
 * Manages domain-specific business logic, data transformation, and lookup access for StudentSessionReport view models.
 */
//todo note that the legacy logic is still persisting. will need writing correctly.

export class DomainManager {
    private lookupStore = container.resolve(LookupStore);

    constructor(
        private viewModel: IStudentSessionReportViewModel,
        private config?: StudentSessionReportManagersConfig,
    ) {}

    /** Map API response for insert mode (previous session context). */
    public mapNewStudentSessionContextResponseToModel(response: GetNewStudentSessionReportContextResponse): void {
        this.initializeNewSessionState(response);
        this.mapMentors(response.mentors);
        this.mapWellbeing(response.studentEngagements);
        this.mapStudentTargets(response.studentTargets);
        this.mapActivityTypes(response.activityTypes);
        this.mapNewStudentSessionBehaviours(response.studentBehaviours);
    }

    /** Map API response for edit mode (existing session report). */
    public mapSessionContextResponseToModel(response: StudentSessionContextByIdResponse): void {
        this.normalizeSessionTimes(response.studentSessionReport);
        this.mapEditSessionState(response);
        this.mapSessionDocuments(response.sessionDocuments);
        this.mapWellbeing(response.studentEngagements);
        this.mapStudentTargets(response.studentTargets);
        this.mapAQACertificates(response.studentSessionAQACertificates);
        this.mapActivityTypes(response.activityTypes);
        this.mapEditStudentSessionBehaviours(response.studentBehaviours);

        this.mapLegacyForm(response);
    }

    /** Map API response after upsert. */
    public mapUpsertResponseToModel(response: StudentSessionContextByIdResponse): void {
        this.normalizeSessionTimes(response.studentSessionReport);
        this.mapEditSessionState(response);
        this.mapSessionDocuments(response.sessionDocuments);
        this.mapWellbeing(response.studentEngagements);
        this.mapStudentTargets(response.studentTargets);
        this.mapAQACertificates(response.studentSessionAQACertificates);
        this.mapActivityTypes(response.activityTypes);
        this.mapEditStudentSessionBehaviours(response.studentBehaviours);
    }

    public buildUpsertStudentSessionRequest(model: StudentSessionContext): UpsertStudentSessionRequest {
        const hasLegacy = this.viewModel.model.isLegacy && this.viewModel.studentSessionLegacyFormViewModel;
        const legacyOverrides = hasLegacy ? this.viewModel.studentSessionLegacyFormViewModel!.model : null;
        return new UpsertStudentSessionRequestBuilder().build(model, legacyOverrides, {
            isAdmin: this.config?.isAdmin ?? true,
        });
    }

    /** Map save activity options response to model and lookup store. */
    public mapSaveActivityOptionsResponseToModel(response: SaveActivityOptionsResponse): void {
        if (response.id) {
            this.viewModel.model.activityTypeId = response.id;
            const activityType = new ActivityType(response.id);
            activityType.name = response.name ?? "";
            activityType.type = response.type ?? "";
            activityType.ordinal = response.ordinal ?? 0;
            activityType.isDeleted = response.isDeleted ?? false;
            this.lookupStore.activityTypes.push(activityType);
        }
    }

    public initializeTargetViewModels(): void {
        this.viewModel.setStudentTargetViewModels(
            this.viewModel.model.studentTargets.map((st) => {
                const vm = new StudentTargetViewModel(st);
                vm._subTargetViewModels.replace(
                    st.subTargets.map((sst) => {
                        const sstVM = new StudentSubTargetViewModel(sst, vm.onSubTargetCompletedByMentorCommandCallback);
                        sstVM._studentSessionSubTargetViewModel = new StudentSessionSubTargetViewModel(
                            sst.studentSessionSubTargetModel!,
                            sstVM.onUpdateSessionReportCallbackCommand,
                        );
                        if (sstVM.hasExistingSession) {
                            sstVM.setValue("isCompletedByMentor", true);
                            sstVM._studentSessionSubTargetViewModel.setIsDisabled(false);
                        }
                        return sstVM;
                    }),
                );
                return vm;
            }),
        );
        this.viewModel.setSessionAQACertificatesViewModel(new StudentSessionAQACertificatesViewModel(this.viewModel.model.studentSessionAQACertificatesModel));
        this.initializeBehaviourViewModels();
    }

    public initializeBehaviourViewModels(): void {
        this.viewModel.setStudentBehaviourViewModels(this.viewModel.model.studentBehaviourModels.map((model) => new StudentBehaviourViewModel(model)));
    }

    public canSubmitForm(): boolean {
        const isFormValid = this.viewModel.isModelValid();
        const isStudentAbsenceValid = this.viewModel.studentAbsenceViewModel.isModelValid();
        const isLegacyFormValid = !!this.viewModel.studentSessionLegacyFormViewModel?.isModelValid();
        const areStudentTargetsValid = this.validateCompletedStudentTargets();
        const areStudentSessionBehaviourModelsValid = this.areStudentSessionBehaviourModelsValid();

        if (this.viewModel.model.isLegacy) {
            return isFormValid && isStudentAbsenceValid && isLegacyFormValid;
        } else {
            return isFormValid && isStudentAbsenceValid && areStudentTargetsValid && areStudentSessionBehaviourModelsValid;
        }
    }

    // ─── Public API: Lookup Getters ───────────────────────────────────────────────────────────

    public get activity(): KeyValuePair[] {
        return this.lookupStore.activityTypeKeyValuePairs;
    }

    public get absenceReasons(): KeyValuePair[] {
        return this.lookupStore.absenceTypeKeyValuePairs;
    }

    public get documentCategories(): KeyValuePair[] {
        return this.lookupStore.documentCategoryKeyValuePairs;
    }

    public get sessionStatuses(): KeyValuePair[] {
        return this.lookupStore.sessionStatusKeyValuePairs;
    }

    public get attendanceOptions(): KeyValuePair[] {
        return [
            { key: "0", text: "Absent" },
            { key: "1", text: "Attended" },
        ] as KeyValuePair[];
    }

    public get mentorKeyValuePairs(): KeyValuePair[] {
        return this.viewModel.model.mentors.map((model: Mentor) => {
            return { key: model.id, text: model.firstName + " " + model.lastName } as KeyValuePair;
        });
    }

    public getSessionStatusById(id: string): string {
        if (this.sessionStatuses.length > 0) {
            const sessionStatus = this.sessionStatuses.find((m) => m.key === id);
            return sessionStatus ? sessionStatus.text : "";
        }
        return "";
    }

    public getDocumentCategoryName(id: string): string | undefined {
        return this.documentCategories.find((m) => m.key === id)?.text;
    }

    public get displayStatus(): string {
        const studentAttendance = this.attendanceOptions.find((m) => m.key === this.viewModel.model.attendance);
        return studentAttendance?.text === "Attended" ? "#27E6A3" : studentAttendance?.text === "Absent" ? "#E6054E" : "";
    }

    public get renderForm(): boolean {
        const studentAttendance = this.attendanceOptions.find((m) => m.key === this.viewModel.model.attendance);
        return studentAttendance?.text !== "Absent";
    }

    public get shouldShowExportButton(): boolean {
        return isEmptyOrWhitespace(this.viewModel.model.id);
    }

    public get displayName(): string {
        return isEmptyOrWhitespace(this.viewModel.model.id) ? "CREATE" : "UPDATE";
    }

    public get emotionalRegulationMentoringProgramme(): string {
        return this.viewModel.model.student.emotionalRegulationMentoringProgramme ?? "";
    }

    public get filteredSessionDocuments(): SessionDocument[] {
        return this.viewModel.model.sessionDocument.slice().filter((m) => !m.isDeleted);
    }

    public get isLegacy(): boolean {
        return this.viewModel.model.isLegacy;
    }

    public get showStudentBehaviours() {
        return this.viewModel.model.student.receivingTargetedIntervention;
    }

    // ─── Private: Orchestration Helpers ───────────────────────────────────────────────────────

    private initializeNewSessionState(response: GetNewStudentSessionReportContextResponse): void {
        // Map primitives from response
        this.viewModel.model.sessionDate = response.sessionDate ?? new Date();
        this.viewModel.model.sessionStartTime = response.sessionStartTime ? convertTimeToDate(response.sessionStartTime) : this.viewModel.model.sessionStartTime;
        this.viewModel.model.sessionEndTime = response.sessionEndTime ? convertTimeToDate(response.sessionEndTime) : this.viewModel.model.sessionEndTime;
        this.viewModel.model.activityTypeId = response.activityTypeId ?? "";
        this.viewModel.model.didAttend = response.didAttend;
        this.viewModel.model.reportIncident = response.reportIncident;

        //todo fix legacy.
        this.viewModel.model.attendance = !response.sessionPrefill?.didAttend ? "0" : "1";
        // Session mentor is only present when entering from a rota session (sessionId supplied).
        // Direct report entry has no pre-assigned mentor, so leave it for the user to select.
        if (response.sessionMentor) {
            //todo fix legacy logic
            this.viewModel.model.mentorId = response.sessionMentor.id!;
        }

        // Map student explicitly
        const studentModel = new StudentModel();
        studentModel.fromResponse(response.student);
        this.viewModel.model.student = studentModel;

        //Map mentor explicitily
        if (response.sessionMentor) {
            const mentorModel = new MentorModel();
            mentorModel.fromResponse(response.sessionMentor);
            this.viewModel.model.sessionMentor = mentorModel;
        }

        if (response.sessionPrefill) {
            const pf = response.sessionPrefill;
            this.viewModel.model.sessionDate = pf.sessionDate ?? new Date();
            this.viewModel.model.sessionStartTime = pf.sessionStartTime ? convertTimeToDate(pf.sessionStartTime) : this.viewModel.model.sessionStartTime;
            this.viewModel.model.sessionEndTime = pf.sessionEndTime ? convertTimeToDate(pf.sessionEndTime) : this.viewModel.model.sessionEndTime;
            this.viewModel.model.activityTypeId = pf.activityTypeId ?? "";
            this.viewModel.model.didAttend = pf.didAttend ?? false;
            this.viewModel.model.reportIncident = pf.reportIncident;
            this.viewModel.model.mentorId = pf.mentorId ?? "";
            this.viewModel.model.activityOptionName = pf.activityTypeName ?? "";
            this.viewModel.studentAbsenceViewModel.setValue("didAttend", pf.didAttend ?? true);
            this.viewModel.model.studentAbsence.didAttend = pf.didAttend ?? true;
        } else {
            this.viewModel.studentAbsenceViewModel.setValue("didAttend", true);
            this.viewModel.model.studentAbsence.didAttend = response.didAttend ?? true;
        }
    }

    private mapEditSessionState(response: StudentSessionContextByIdResponse): void {
        const report = response.studentSessionReport;
        this.viewModel.model.fromResponse(report);

        //todo fix legacy.
        this.viewModel.model.attendance = !report.didAttend ? "0" : "1";

        //todo fix legacy logic
        this.viewModel.model.mentorId = response.mentor.id;

        const studentModel = new StudentModel();
        studentModel.fromResponse(response.student);
        this.viewModel.model.student = studentModel;

        const sessionMentorModel = new MentorModel();
        sessionMentorModel.fromResponse(response.mentor);
        this.viewModel.model.sessionMentor = sessionMentorModel;

        if (!report.didAttend) {
            const studentAbsenceModel = new StudentAbsenceModel();
            studentAbsenceModel.fromResponse(response.studentSessionAbsence);
            this.viewModel.model.studentAbsence = studentAbsenceModel;
        }

        this.viewModel.model.studentAbsence.didAttend = report.didAttend;

        this.handleAbsenceTypeDisplay(report);
    }

    private normalizeSessionTimes(report: StudentSessionReportResponse): void {
        report.sessionStartTime = convertTimeToDate(report.sessionStartTime).toISOString() as any;
        report.sessionEndTime = convertTimeToDate(report.sessionEndTime).toISOString() as any;
    }

    private mapStudentTargets(studentTargets: StudentTargetResponse[]): void {
        this.viewModel.model.studentTargets.replace(
            studentTargets.map((str) => {
                const model = new StudentTargetModel();
                model.fromResponse(str);
                model.studentSessionTargetModel = new StudentSessionTargetModel();
                model.studentSessionTargetModel.fromResponse(str.studentSessionTarget);
                model.subTargets.replace(
                    str.subTargets.map((sstr) => {
                        const subTarget = new StudentSubTargetModel();
                        subTarget.fromResponse(sstr);
                        subTarget.studentSessionSubTargetModel = new StudentSessionSubTargetModel();
                        subTarget.studentSessionSubTargetModel.fromResponse(sstr.studentSessionSubTarget);
                        return subTarget;
                    }),
                );
                return model;
            }),
        );
    }

    private mapActivityTypes(activityTypes: ActivityType[] | undefined): void {
        if (activityTypes) {
            this.lookupStore.activityTypes.replace(
                activityTypes.map((model: ActivityType) => {
                    return { id: model.id, name: model.name, type: model.type, ordinal: model.ordinal, isDeleted: model.isDeleted } as ActivityType;
                }),
            );
        }
    }

    private mapNewStudentSessionBehaviours(response: StudentBehaviourResponse[]): void {
        this.viewModel.model.studentBehaviourModels.replace(
            response.map((r) => {
                const m = new StudentBehaviourModel();
                m.fromResponse(r);
                m.studentSessionBehaviour = new StudentSessionBehaviourModel();
                m.studentSessionBehaviour.studentBehaviourId = m.id as string;
                return m;
            }),
        );
    }

    private mapEditStudentSessionBehaviours(response: StudentBehaviourWithSessionResponse[]): void {
        this.viewModel.model.studentBehaviourModels.replace(
            response.map((r) => {
                const m = new StudentBehaviourModel();
                m.fromResponse(r);
                m.studentSessionBehaviour = new StudentSessionBehaviourModel();
                m.studentSessionBehaviour.fromResponse(r.studentSessionBehaviour);

                return m;
            }),
        );
    }

    private mapMentors(mentors: any[]): void {
        this.viewModel.model.mentors.replace(
            mentors.map((e: any) => {
                const model = new Mentor();
                model.fromResponse(e);
                return model;
            }),
        );
    }

    private mapWellbeing(engagements: any[]): void {
        this.viewModel.model.wellbeing.replace(
            engagements
                .sort((a: any, b: any) => (a.ordinal > b.ordinal ? 1 : b.ordinal > a.ordinal ? -1 : 0))
                .map((e: any) => {
                    const model = new StudentEngagementModel();
                    model.fromResponse(e);
                    return model;
                }),
        );
    }

    private mapSessionDocuments(documents: any[]): void {
        this.viewModel.model.sessionDocument.replace(
            documents.map((e: any) => {
                const model = new SessionDocument();
                model.fromResponse(e);
                return model;
            }),
        );
    }

    private mapAQACertificates(certificates: any[] | undefined): void {
        if (certificates) {
            this.viewModel.model.studentSessionAQACertificatesModel.certificates.replace(
                certificates.map((e: any) => {
                    const model = new StudentSessionAQACertificateModel();
                    model.fromResponse(e);
                    return model;
                }),
            );
        }
    }

    private mapLegacyForm(response: StudentSessionContextByIdResponse): void {
        if (response.studentSessionReport.isLegacy) {
            const model = new StudentSessionLegacyFormModel(this.viewModel.model);
            model.fromResponse(response.studentSessionReport);
            this.viewModel.setLegacyFormViewModel(new StudentSessionLegacyFormViewModel(model));
        }
    }

    private handleAbsenceTypeDisplay(report: StudentSessionReportResponse): void {
        if (!report.didAttend && this.viewModel.model.studentAbsence.absenseTypeId) {
            const absenceType = this.lookupStore.absenceTypes.find((t) => t.id === this.viewModel.model.studentAbsence.absenseTypeId);
            this.viewModel.managers?.mobx?.setShowOtherAbsenceText(absenceType?.name === "Other");
        }
    }

    private validateCompletedStudentTargets(): boolean {
        const completedTargets = this.viewModel.studentTargetViewModels.slice().filter((st) => st.subTargetViewModels.some((sst) => sst.model.isCompletedByMentor));
        completedTargets.forEach((st) => {
            st.validateModel();
        });
        return completedTargets.every((st) => st.isModelValid());
    }

    private areStudentSessionBehaviourModelsValid(): boolean {
        if (!this.viewModel.model.student.receivingTargetedIntervention) return true;
        const viewModels = this.viewModel.studentBehaviourViewModels;
        viewModels.forEach((vm) => vm.validateSessionBehaviourModel());
        return viewModels.every((vm) => vm.isSessionBehaviourModelValid());
    }
}
